import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiZap, FiAward, FiArrowRight, FiCheckCircle, FiXCircle, FiTrendingUp } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const quizData = [
  {
    question: "Choose the correct form of 'to be' in present tense",
    questionEs: "¿Cuál es la forma correcta de 'ser/estar' en presente?",
    options: ["I am happy", "I is happy", "I are happy", "I be happy"],
    correct: 0,
    hint: "Primera persona singular"
  },
  {
    question: "What is the past tense of 'eat'?",
    questionEs: "¿Cuál es el pasado de 'comer'?",
    options: ["eated", "ate", "eaten", "eating"],
    correct: 1,
    hint: "Verbo irregular"
  },
  {
    question: "Choose the correct sentence",
    questionEs: "Elige la oración correcta",
    options: ["She don't like coffee", "She doesn't like coffee", "She do not likes coffee", "She not like coffee"],
    correct: 1,
    hint: "Tercera persona singular en negativo"
  },
  {
    question: "What does 'embarrassed' mean?",
    questionEs: "¿Qué significa 'embarrassed'?",
    options: ["Enojado", "Avergonzado", "Confundido", "Asustado"],
    correct: 1,
    hint: "Emoción común"
  },
  {
    question: "Complete: 'If I _____ you, I would help'",
    questionEs: "Completa: 'Si yo te _____, te ayudaría'",
    options: ["was", "were", "am", "be"],
    correct: 1,
    hint: "Segundo condicional"
  }
  // ... more questions can be added here
];

const EnglishQuiz = () => {
  const [current, setCurrent] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [points, setPoints] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);

  const level = Math.floor(points / 50) + 1;
  const progress = ((current + 1) / quizData.length) * 100;

  const handleAnswer = (index) => {
    if (answered) return;
    setSelected(index);
    setAnswered(true);

    if (index === quizData[current].correct) {
      setScore(score + 1);
      setStreak(streak + 1);
      setPoints(points + 10 + (streak * 2));
    } else {
      setStreak(0);
    }
  };

  const nextQuestion = () => {
    if (current + 1 < quizData.length) {
      setCurrent(current + 1);
      setSelected(null);
      setAnswered(false);
    } else {
      setIsFinished(true);
    }
  };

  const resetQuiz = () => {
    setCurrent(0);
    setScore(0);
    setStreak(0);
    setPoints(0);
    setIsFinished(false);
    setSelected(null);
    setAnswered(false);
  };

  if (isFinished) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-zinc-900 border-2 border-yellow-500 rounded-[2.5rem] p-8 text-center shadow-2xl">
        <div className="text-6xl mb-4">🎉</div>
        <h3 className="text-3xl font-black text-white mb-2 uppercase italic leading-none">¡Misión Cumplida!</h3>
        <div className="text-5xl font-black text-green-500 mb-6">{score}/{quizData.length}</div>
        
        <div className="bg-black/50 rounded-2xl p-6 mb-8 border border-zinc-800">
           <div className="flex justify-between items-center mb-4">
              <div className="text-left">
                <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">Nivel Final</p>
                <p className="text-2xl font-black text-yellow-500">LVL {level}</p>
              </div>
              <SafeIcon icon={FiAward} className="text-4xl text-yellow-500" />
           </div>
           <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-yellow-500 to-green-500" style={{ width: `${(points % 50) / 50 * 100}%` }}></div>
           </div>
           <p className="text-xs text-gray-400 mt-2 font-bold uppercase">{points} XP TOTALES</p>
        </div>

        <div className="grid gap-4">
          <a href="https://app.onlineclassesenglish.com" className="bg-yellow-500 text-black py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-yellow-400 transition-all shadow-xl">Reclamar Certificado</a>
          <button onClick={resetQuiz} className="text-gray-500 font-bold uppercase text-xs hover:text-white transition-colors">Volver a Intentar</button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden group">
      <div className="flex justify-between items-center mb-8">
        <div className="flex gap-4">
          <div className="bg-black px-4 py-2 rounded-xl border border-zinc-800 text-center">
            <p className="text-[9px] text-gray-500 font-bold uppercase mb-1">Racha</p>
            <div className="flex items-center gap-1 text-orange-500 font-black">
              <SafeIcon icon={FiZap} className="animate-pulse" /> {streak}
            </div>
          </div>
          <div className="bg-black px-4 py-2 rounded-xl border border-zinc-800 text-center">
            <p className="text-[9px] text-gray-500 font-bold uppercase mb-1">Nivel</p>
            <div className="text-blue-500 font-black">{level}</div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-gray-500 font-bold uppercase mb-1">Puntos XP</p>
          <div className="text-green-500 font-black">{points}</div>
        </div>
      </div>

      <div className="w-full h-1.5 bg-black rounded-full mb-8 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-red-600 via-yellow-500 to-green-600" animate={{ width: `${progress}%` }} />
      </div>

      <div className="mb-8">
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.2em] mb-2">Pregunta {current + 1} de {quizData.length}</p>
        <h4 className="text-white text-xl font-black uppercase leading-tight italic mb-4">{quizData[current].question}</h4>
        <div className="bg-black/50 border-l-4 border-yellow-500 p-4 rounded-r-xl">
          <p className="text-xs text-gray-400 italic">💡 {quizData[current].questionEs}</p>
          <p className="text-[10px] text-yellow-500 font-bold uppercase mt-1">Pista: {quizData[current].hint}</p>
        </div>
      </div>

      <div className="grid gap-3 mb-8">
        {quizData[current].options.map((opt, i) => (
          <button
            key={i}
            onClick={() => handleAnswer(i)}
            disabled={answered}
            className={`w-full text-left p-4 rounded-2xl text-sm font-bold transition-all border-2 flex justify-between items-center ${
              selected === i 
                ? (i === quizData[current].correct ? 'border-green-500 bg-green-900/20 text-white' : 'border-red-500 bg-red-900/20 text-white') 
                : (answered && i === quizData[current].correct ? 'border-green-500 bg-green-900/10 text-white' : 'border-zinc-800 bg-black text-gray-400 hover:border-zinc-600')
            }`}
          >
            {opt}
            {answered && i === quizData[current].correct && <SafeIcon icon={FiCheckCircle} className="text-green-500 text-lg" />}
            {answered && selected === i && i !== quizData[current].correct && <SafeIcon icon={FiXCircle} className="text-red-500 text-lg" />}
          </button>
        ))}
      </div>

      <button
        onClick={nextQuestion}
        disabled={!answered}
        className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${
          answered ? 'bg-white text-black hover:bg-yellow-400' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
        }`}
      >
        Siguiente Nivel <FiArrowRight />
      </button>
    </div>
  );
};

export default EnglishQuiz;